import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, Button } from "../components/ui";
import { useAuth } from "../components/AuthProvider";
import { useI18n } from "../i18n";
import apiService from "../services/api";
import { pageScope } from "../lib/cssScope";

type SurveyQuestion = {
  id: string;
  code?: string;
  text_en?: string | null;
  text_es?: string | null;
  text_ru?: string | null;
  type?: string | null;
  scaleMin?: number | null;
  scaleMax?: number | null;
  weight?: number | null;
};

type SurveyCategory = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  questions: SurveyQuestion[];
};

type SurveyLoadState = "idle" | "loading" | "error" | "success";

const mapStandardToBackend = (value: string): string => {
  const normalized = value
    .toLowerCase()
    .replace("iso-27001", "iso_27001")
    .replace("pci-dss-2025", "pci_dss_2025")
    .replace("swift-cscf-2025", "swift_cscf_2025");

  switch (normalized) {
    case "iso_27001":
      return "ISO_27001";
    case "pci_dss_2025":
      return "PCI_DSS_2025";
    case "nis2":
      return "NIS2";
    case "soc2":
      return "SOC2";
    case "swift_cscf_2025":
      return "SWIFT_CSCF_2025";
    case "dora":
      return "DORA";
    case "gdpr":
      return "GDPR";
    default:
      return normalized.toUpperCase();
  }
};

const getScaleValues = (question: SurveyQuestion): number[] => {
  const min = Number.isFinite(question.scaleMin) ? Number(question.scaleMin) : 1;
  const max = Number.isFinite(question.scaleMax) ? Number(question.scaleMax) : 5;
  if (min >= max) {
    return [Math.round(min)];
  }
  const values: number[] = [];
  for (let current = Math.round(min); current <= Math.round(max); current += 1) {
    values.push(current);
  }
  return values;
};

const calculateWeightedScore = (
  categories: SurveyCategory[],
  answers: Record<string, number>,
): number => {
  let total = 0;
  let weightSum = 0;
  categories.forEach((category) => {
    category.questions.forEach((question) => {
      const value = answers[question.id];
      if (typeof value !== "number" || Number.isNaN(value) || value <= 0) return;
      const weight = question.weight ?? 1;
      weightSum += weight;
      total += value * weight;
    });
  });
  if (weightSum <= 0) return 0;
  return Number((total / weightSum).toFixed(1));
};

const questionLabel = (
  question: SurveyQuestion,
  language: string,
): string => {
  if (language === "ru" && question.text_ru) return question.text_ru;
  if (language === "es" && question.text_es) return question.text_es;
  if (question.text_en) return question.text_en;
  return question.text_ru || question.text_es || question.code || question.id;
};

/**
 * @deprecated ComplianceSurvey (short questionnaires) deprecated in favor of Extended questionnaires.
 * This page redirects to the main Compliance page with Full Assessment.
 */
const ComplianceSurvey: React.FC = () => {
  const { standard = "gdpr" } = useParams();
  const backendStandard = useMemo(
    () => mapStandardToBackend(String(standard)),
    [standard],
  );
  const navigate = useNavigate();

  // Redirect to main Compliance page (Extended-only)
  useEffect(() => {
    const standardParam = standard ? `?standard=${encodeURIComponent(backendStandard)}` : '';
    navigate(`/compliance${standardParam}`, { replace: true });
  }, [navigate, standard, backendStandard]);
  const { token, user } = useAuth();
  const { t, language } = useI18n();

  const primaryOrg =
    Array.isArray(user?.organizations) && user?.organizations.length
      ? (user!.organizations[0] as { id?: string })
      : null;
  const organizationId = primaryOrg?.id ? String(primaryOrg.id) : null;

  const [categories, setCategories] = useState<SurveyCategory[]>([]);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  const [score, setScore] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<SurveyLoadState>("idle");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const loadSurvey = useCallback(async () => {
    if (!token) return;
    setStatus("loading");
    setError(null);
    setStatusMessage(null);

    try {
      if (!organizationId) {
        throw new Error(
          t(
            "compliance.survey.errors.organizationMissing",
            "Organization is not defined for the current user.",
          ),
        );
      }

      const [{ categories: apiCategories }, survey] = await Promise.all([
        apiService.getComplianceCategories({
          organizationId,
          standardCode: backendStandard,
        }),
        apiService
          .getComplianceSurvey(backendStandard, organizationId)
          .catch(() => null),
      ]);

      const surveyPayload =
        survey && typeof survey === "object" && "data" in (survey as any)
          ? (survey as any).data ?? {}
          : survey ?? {};

      const normalizedCategories: SurveyCategory[] = (apiCategories || []).map(
        (category) => ({
          id: category.id,
          code: category.code,
          name: category.name,
          description: category.description ?? null,
          questions: Array.isArray(category.questions)
            ? category.questions.map((question) => ({
                id: question.id,
                code: question.code,
                text_en: question.text_en ?? null,
                text_es: question.text_es ?? null,
                text_ru: question.text_ru ?? null,
                type: question.type ?? "radio",
                scaleMin:
                  typeof question.scaleMin === "number"
                    ? question.scaleMin
                    : 1,
                scaleMax:
                  typeof question.scaleMax === "number"
                    ? question.scaleMax
                    : 5,
                weight:
                  typeof question.weight === "number"
                    ? question.weight
                    : 1,
              }))
            : [],
        }),
      );

      const existingAnswers =
        surveyPayload &&
        typeof (surveyPayload as any).answers === "object" &&
        surveyPayload !== null
          ? (surveyPayload as any).answers
          : {};

      const filteredAnswers: Record<string, number> = {};
      normalizedCategories.forEach((category) =>
        category.questions.forEach((question) => {
          const raw = existingAnswers[question.id];
          const numeric = Number(raw);
          if (Number.isFinite(numeric) && numeric > 0) {
            filteredAnswers[question.id] = numeric;
          }
        }),
      );

      setCategories(normalizedCategories);
      setAnswers(filteredAnswers);
      setSubmittedAt(
        (surveyPayload && (surveyPayload as any).submitted_at) || null,
      );
      setUpdatedBy((surveyPayload && (surveyPayload as any).updated_by) || null);

      const initialScore =
        typeof (surveyPayload as any)?.compliance_score === "number"
          ? Number((surveyPayload as any).compliance_score)
          : calculateWeightedScore(normalizedCategories, filteredAnswers);
      setScore(initialScore);

      setStatus("success");
    } catch (err) {
      console.error("[ComplianceSurvey] load failed", err);
      setCategories([]);
      setAnswers({});
      setScore(0);
      setSubmittedAt(null);
      setUpdatedBy(null);
      setStatus("error");
      setError(
        err instanceof Error
          ? err.message
          : t("compliance.survey.errors.load", "Failed to load survey data"),
      );
    }
  }, [backendStandard, organizationId, t, token]);

  useEffect(() => {
    void loadSurvey();
  }, [loadSurvey]);

  const handleAnswerChange = (questionId: string, value: number) => {
    setAnswers((prev) => {
      const next = { ...prev, [questionId]: value };
      setScore(calculateWeightedScore(categories, next));
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!organizationId) return;
    try {
      setStatus("loading");
      setError(null);
      setStatusMessage(null);

      const payload = {
        organization_id: organizationId,
        standard: backendStandard,
        compliance_score: calculateWeightedScore(categories, answers),
        answers,
        submitted_at: new Date().toISOString(),
      };

      const response = await apiService.saveComplianceSurvey(payload);
      if (response && response.success === false) {
        throw new Error(response.message || "Failed to save survey");
      }

      const newSubmittedAt = new Date().toISOString();
      setSubmittedAt(newSubmittedAt);
      setUpdatedBy(user?.fullName || user?.username || null);
      setStatus("success");
      setStatusMessage(
        t(
          "compliance.survey.messages.saved",
          "Questionnaire has been saved successfully.",
        ),
      );
    } catch (err) {
      console.error("[ComplianceSurvey] save failed", err);
      setStatus("error");
      setStatusMessage(null);
      setError(
        err instanceof Error
          ? err.message
          : t("compliance.survey.errors.save", "Failed to save survey"),
      );
    } finally {
      setStatus("idle");
    }
  };

  const isLoading = status === "loading";
  const hasCategories = categories.length > 0;

  return (
    <div className={`${pageScope("compliance-survey")} space-y-4 p-6`}>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          {t("compliance.survey.title", "Compliance Survey: {standard}", {
            standard: backendStandard,
          })}
        </h1>
        <Button
          type="button"
          color="outline"
          size="small"
          onClick={() => navigate("/compliance")}
          className="border-transparent bg-transparent text-sm text-blue-600 shadow-none hover:underline"
        >
          {t("compliance.survey.hide", "Back to Compliance")}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="text-sm text-gray-600">
            {t("compliance.survey.lastSavedLabel", "Last saved")}:{" "}
            {submittedAt
              ? new Date(submittedAt).toLocaleString()
              : t("compliance.survey.notAvailable", "—")}
            {updatedBy
              ? ` · ${t("compliance.survey.byUser", "by {user}", {
                  user: String(updatedBy),
                })}`
              : ""}
          </div>
          <div className="text-sm text-gray-800">
            {t("compliance.survey.currentScoreLabel", "Current score")}:{" "}
            <span className="font-semibold">{score.toFixed(1)}</span>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {isLoading && (
            <div className="text-sm text-gray-600">
              {t("compliance.survey.loading", "Loading questionnaire...")}
            </div>
          )}

          {!isLoading && error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {!isLoading && !error && !hasCategories && (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {t(
                "compliance.survey.empty",
                "No categories are available for this standard yet.",
              )}
            </div>
          )}

          {!isLoading &&
            !error &&
            hasCategories &&
            categories.map((category) => (
              <section
                key={category.id}
                className="space-y-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
              >
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {category.name}
                  </h2>
                  {category.description && (
                    <p className="mt-1 text-sm text-gray-600">
                      {category.description}
                    </p>
                  )}
                </div>

                <div className="space-y-4">
                  {category.questions.map((question) => {
                    const scaleValues = getScaleValues(question);
                    const selectedValue = answers[question.id] ?? 0;
                    const label = questionLabel(question, language);

                    return (
                      <div
                        key={question.id}
                        className="rounded-lg border border-gray-200 p-4"
                      >
                        <div className="mb-3 text-sm font-medium text-gray-800">
                          {label}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                          {scaleValues.map((value) => (
                            <label
                              key={value}
                              className="flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 px-3 py-1 transition hover:border-blue-400"
                            >
                              <input
                                type="radio"
                                name={question.id}
                                value={value}
                                checked={selectedValue === value}
                                onChange={() =>
                                  handleAnswerChange(question.id, value)
                                }
                                className="accent-blue-600"
                              />
                              <span>{value}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              color="primary"
              onClick={handleSubmit}
              disabled={isLoading || categories.length === 0}
            >
              {isLoading
                ? t("compliance.survey.saving", "Saving...")
                : t("compliance.survey.save", "Save questionnaire")}
            </Button>
            <Button
              type="button"
              color="secondary"
              onClick={() => navigate("/compliance")}
            >
              {t("compliance.survey.cancel", "Cancel")}
            </Button>
            {statusMessage && (
              <span className="text-sm text-green-600">{statusMessage}</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComplianceSurvey;
